import React, { useEffect, useState } from 'react';
import { useKanban } from '../../contexts/KanbanContext';
import { Tarefa } from '../../types';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { obterCorPrioridade } from '../../utils/coresPrioridade';
import SkeletonLoader from '../common/SkeletonLoader';

// Componente para formatar a prioridade com cores — mesma paleta usada em
// todo o resto do sistema, não mais as cores genéricas do MUI.
const PrioridadeChip: React.FC<{ prioridade: string }> = ({ prioridade }) => {
  const cor = obterCorPrioridade(prioridade);
  return (
    <Chip
      label={prioridade.charAt(0).toUpperCase() + prioridade.slice(1)}
      size="small"
      variant="outlined"
      sx={{ color: cor, borderColor: cor }}
    />
  );
};

// Componente para formatar o status com cores
const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const getColor = () => {
    switch (status.toLowerCase()) {
      case 'pendente': return 'default';
      case 'em andamento': return 'primary';
      case 'aguardando aprovação': return 'warning';
      case 'concluída': return 'success';
      case 'finalizada': return 'success';
      default: return 'default';
    }
  };

  return (
    <Chip 
      label={status} 
      color={getColor() as any} 
      size="small" 
    />
  );
};

const TarefasArquivadas: React.FC = () => {
  const { 
    tarefasArquivadas, 
    carregandoArquivadas, 
    totalPaginasArquivadas,
    paginaAtualArquivadas,
    desarquivarTarefa,
    carregarTarefasArquivadas,
    erro
  } = useKanban();
  
  const navigate = useNavigate();
  const [paginaLocal, setPaginaLocal] = useState(paginaAtualArquivadas);
  
  // Estados para os filtros de busca
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPrioridade, setFiltroPrioridade] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroCriador, setFiltroCriador] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [tarefasFiltradas, setTarefasFiltradas] = useState<Tarefa[]>([]);
  const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState(false);

  // Inicializar a página local com a página atual do contexto
  useEffect(() => {
    setPaginaLocal(paginaAtualArquivadas);
  }, [paginaAtualArquivadas]);
  
  // Carregar tarefas arquivadas sempre que o componente montar
  // Isso garante que os dados sejam carregados apenas quando o usuário acessa a página de tarefas arquivadas
  useEffect(() => {
    // Sempre carregamos as tarefas arquivadas quando o componente é montado
    // independentemente se já temos dados ou não
    carregarTarefasArquivadas(paginaAtualArquivadas);
    
    // Retornar função de limpeza para componente não montado
    return () => {
      // Não é necessário fazer nada aqui, mas poderia ser usado para cancelar requisições pendentes
    };
  }, []);
  
  // Efeito para filtrar as tarefas quando os filtros ou as tarefas mudam
  useEffect(() => {
    filtrarTarefas();
  }, [tarefasArquivadas, busca, filtroStatus, filtroPrioridade, filtroResponsavel, filtroCriador, filtroDataInicio, filtroDataFim]);
  
  // Função para filtrar as tarefas com base nos critérios de busca
  const filtrarTarefas = () => {
    let resultado = [...tarefasArquivadas];
    
    // Filtrar por texto de busca (título ou descrição)
    if (busca) {
      const termoBusca = busca.toLowerCase();
      resultado = resultado.filter(tarefa => 
        tarefa.titulo.toLowerCase().includes(termoBusca) || 
        (tarefa.descricao && tarefa.descricao.toLowerCase().includes(termoBusca))
      );
    }
    
    // Filtrar por status
    if (filtroStatus) {
      resultado = resultado.filter(tarefa => 
        tarefa.status.toLowerCase() === filtroStatus.toLowerCase()
      );
    }
    
    // Filtrar por prioridade
    if (filtroPrioridade) {
      resultado = resultado.filter(tarefa => 
        tarefa.prioridade.toLowerCase() === filtroPrioridade.toLowerCase()
      );
    }
    
    // Filtrar por responsável
    if (filtroResponsavel) {
      resultado = resultado.filter(tarefa => 
        tarefa.responsavel.nome.toLowerCase().includes(filtroResponsavel.toLowerCase())
      );
    }
    
    // Filtrar por criador
    if (filtroCriador) {
      resultado = resultado.filter(tarefa => 
        tarefa.criador.nome.toLowerCase().includes(filtroCriador.toLowerCase())
      );
    }
    
    // Filtrar por data de início (data de criação)
    if (filtroDataInicio) {
      const dataInicio = new Date(filtroDataInicio);
      dataInicio.setHours(0, 0, 0, 0);
      resultado = resultado.filter(tarefa => {
        if (!tarefa.dataCriacao) return false;
        const dataCriacao = new Date(tarefa.dataCriacao);
        return dataCriacao >= dataInicio;
      });
    }
    
    // Filtrar por data de fim (data de criação)
    if (filtroDataFim) {
      const dataFim = new Date(filtroDataFim);
      dataFim.setHours(23, 59, 59, 999);
      resultado = resultado.filter(tarefa => {
        if (!tarefa.dataCriacao) return false;
        const dataCriacao = new Date(tarefa.dataCriacao);
        return dataCriacao <= dataFim;
      });
    }
    
    setTarefasFiltradas(resultado);
  };
  
  // Handlers para os campos de filtro
  const handleBuscaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusca(e.target.value);
  };
  
  const handleStatusChange = (e: SelectChangeEvent) => {
    setFiltroStatus(e.target.value);
  };
  
  const handlePrioridadeChange = (e: SelectChangeEvent) => {
    setFiltroPrioridade(e.target.value);
  };
  
  const handleResponsavelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroResponsavel(e.target.value);
  };
  
  const handleCriadorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroCriador(e.target.value);
  };
  
  const handleDataInicioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroDataInicio(e.target.value);
  };
  
  const handleDataFimChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFiltroDataFim(e.target.value);
  };
  
  const toggleFiltrosAvancados = () => {
    setMostrarFiltrosAvancados(!mostrarFiltrosAvancados);
  };
  
  const limparFiltros = () => {
    setBusca('');
    setFiltroStatus('');
    setFiltroPrioridade('');
    setFiltroResponsavel('');
    setFiltroCriador('');
    setFiltroDataInicio('');
    setFiltroDataFim('');
  };

  const handleChangePagina = (event: React.ChangeEvent<unknown>, value: number) => {
    setPaginaLocal(value);
    carregarTarefasArquivadas(value);
  };

  const handleDesarquivar = async (tarefaId: string) => {
    await desarquivarTarefa(tarefaId);
  };

  const handleVerDetalhes = (tarefaId: string) => {
    navigate(`/tarefas/${tarefaId}`);
  };

  const formatarData = (dataString: string | null) => {
    if (!dataString) return '-';
    try {
      return format(new Date(dataString), 'dd/MM/yyyy');
    } catch (error) {
      return '-';
    }
  };

  if (carregandoArquivadas) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {erro}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Tarefas Arquivadas
      </Typography>
      
      {/* Campo de busca e filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
            <TextField
              fullWidth
              label="Buscar por título ou descrição"
              variant="outlined"
              size="small"
              value={busca}
              onChange={handleBuscaChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            </Box>
            <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' }, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={toggleFiltrosAvancados}
              sx={{ mr: 1 }}
            >
              {mostrarFiltrosAvancados ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
            {mostrarFiltrosAvancados && (
              <Button 
                variant="text" 
                onClick={limparFiltros}
              >
                Limpar Filtros
              </Button>
            )}
            </Box>
          </Box>
          
          {/* Filtros avançados */}
          {mostrarFiltrosAvancados && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(25% - 12px)' } }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filtro-status-label">Status</InputLabel>
                  <Select
                    labelId="filtro-status-label"
                    value={filtroStatus}
                    label="Status"
                    onChange={handleStatusChange}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Pendente">Pendente</MenuItem>
                    <MenuItem value="Em Andamento">Em Andamento</MenuItem>
                    <MenuItem value="Concluída">Concluída</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(25% - 12px)' } }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="filtro-prioridade-label">Prioridade</InputLabel>
                  <Select
                    labelId="filtro-prioridade-label"
                    value={filtroPrioridade}
                    label="Prioridade"
                    onChange={handlePrioridadeChange}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="Baixa">Baixa</MenuItem>
                    <MenuItem value="Media">Média</MenuItem>
                    <MenuItem value="Alta">Alta</MenuItem>
                    <MenuItem value="Urgente">Urgente</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(25% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Responsável"
                  variant="outlined"
                  size="small"
                  value={filtroResponsavel}
                  onChange={handleResponsavelChange}
                />
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(25% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Criador"
                  variant="outlined"
                  size="small"
                  value={filtroCriador}
                  onChange={handleCriadorChange}
                />
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                <TextField
                  fullWidth
                  label="Data de criação (início)"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={filtroDataInicio}
                  onChange={handleDataInicioChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 'calc(50% - 8px)' } }}>
                <TextField
                  fullWidth
                  label="Data de criação (fim)"
                  type="date"
                  variant="outlined"
                  size="small"
                  value={filtroDataFim}
                  onChange={handleDataFimChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
      
      {tarefasArquivadas.length === 0 ? (
        <Alert severity="info">
          Não há tarefas arquivadas.
        </Alert>
      ) : tarefasFiltradas.length === 0 ? (
        <Alert severity="info">
          Nenhuma tarefa corresponde aos filtros aplicados.
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Prioridade</TableCell>
                  <TableCell>Responsável</TableCell>
                  <TableCell>Criador</TableCell>
                  <TableCell>Data Criação</TableCell>
                  <TableCell>Data Vencimento</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tarefasFiltradas.map((tarefa) => (
                  <TableRow key={tarefa.id}>
                    <TableCell>{tarefa.titulo}</TableCell>
                    <TableCell>
                      <StatusChip status={tarefa.status} />
                    </TableCell>
                    <TableCell>
                      <PrioridadeChip prioridade={tarefa.prioridade} />
                    </TableCell>
                    <TableCell>{tarefa.responsavel.nome}</TableCell>
                    <TableCell>{tarefa.criador.nome}</TableCell>
                    <TableCell>{formatarData(tarefa.dataCriacao)}</TableCell>
                    <TableCell>{formatarData(tarefa.dataVencimento)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton 
                          size="small" 
                          onClick={() => handleVerDetalhes(tarefa.id)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Desarquivar">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDesarquivar(tarefa.id)}
                          color="secondary"
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPaginasArquivadas} 
              page={paginaLocal} 
              onChange={handleChangePagina} 
              color="primary" 
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default TarefasArquivadas;
